'use client'

import React, { useEffect, useState, useRef } from 'react'
import { RecommendedService } from '@/lib/api/services/recommended'
import { useAuth } from '@/hooks/useAuth'
import CarouselContents from '@/components/Shared/CarouselContents'
import Loading from '@/components/Shared/Loading'
import TitleWithViewAll from '@/components/Shared/Title/TitleWithViewAll'
import { RecommendedContent, RecommendedContentsProps } from '@/types/recommended'
import { getViewedArticles } from '@/utility/viewedArticles'
import styles from './styles.module.scss'

const RecommendedContents: React.FC<RecommendedContentsProps> = ({
  used,
  display,
  onRecommendationResponse,
  showTitle = false,
  title = 'あなたへのおすすめ',
}) => {
  const [recommendAi, setRecommendAi] = useState<RecommendedContent[]>([])
  const [showLoading, setShowLoading] = useState(true)
  const { getMemberId } = useAuth()

  // Use refs to prevent multiple API calls like MyPageClient does
  const isLoadingRef = useRef(false)
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    // Prevent multiple API calls using ref-based approach like MyPageClient
    if (hasLoadedRef.current || isLoadingRef.current) {
      return
    }

    const fetchRecommendedContents = async () => {
      // Request deduplication - prevent concurrent requests
      if (isLoadingRef.current) {
        return
      }

      isLoadingRef.current = true
      hasLoadedRef.current = true

      try {
        setShowLoading(true)

        // 閲覧したarticleのIDを取得
        const viewedArticles = getViewedArticles()

        const params: { return_num: number; target_ids?: string } = {
          return_num: 10,
        }

        // ログインしていない場合のみ、target_idsをパラメータに追加
        const memberId = getMemberId()
        if (!memberId && viewedArticles.length > 0) {
          params.target_ids = viewedArticles.join(',')
        }

        const aiData = await RecommendedService.getAIRecommendedContents(params)

        // Ensure we have an array
        const contents = Array.isArray(aiData) ? aiData : []

        setRecommendAi(contents)
        onRecommendationResponse?.(contents)
      } catch (err) {
        setRecommendAi([])
        onRecommendationResponse?.([])
      } finally {
        setShowLoading(false)
        isLoadingRef.current = false
      }
    }

    fetchRecommendedContents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps intentional - using ref-based approach to prevent infinite loops

  // Don't render anything if loading is complete and there are no recommendations
  if (!showLoading && recommendAi.length === 0) {
    return null
  }

  return (
    <div className={styles.recommendContents}>
      {showTitle && recommendAi.length > 0 && (
        <TitleWithViewAll path='' title={title} viewAll={false} />
      )}
      {showLoading ? (
        <div className='loadingWrapper'>
          <Loading />
        </div>
      ) : recommendAi.length > 0 ? (
        <div className={styles.categoryWrapper}>
          <CarouselContents
            items={recommendAi as any}
            type='article'
            used={used}
            recommend
            display={display}
          />
        </div>
      ) : null}
    </div>
  )
}

export default RecommendedContents
